// EDEN · one place that answers "which lesson is this class on this week?" and turns the
// curriculum's internal codes into plain words for teachers.
//
// Home, This Week, the Year plan, Team and the worksheet picker all call resolveClassLesson(), so they
// can never disagree. The rule (owner ruling A2, 21 Oct 2026): "this week" is the SCHOOL CALENDAR's week,
// the same for every teacher and every class of a grade — the year plan's step for that term and week.
// A class's plans never move it (they used to: the pulse jumped to "the class's furthest plan + 1", so two
// classes of one grade pulsed on different weeks). Plans still mark the weeks they cover — `pos` — and a
// page may say how far a class's plans reach, but never which week it is. A week the grade's year plan has
// no step for shows the nearest earlier step with `noStep` set, so the page can say so. Between terms the next
// term's first week is shown as "First week"; before a term starts, its first week, as a preview.

import { api } from "./api";
import {
  loadGrade,
  loadVocab,
  gradeFromClass,
  termNumber,
  schoolWeekNode,
  classPosition,
  resolveSets,
  type CurriculumNode,
  type GradeCurriculum,
  type NodePlanState,
  type ClassPosition,
  type VocabRegister,
} from "./curriculum";
import type { NodeThread } from "./curriculum";
import { t } from "./i18n";
import { fmtDate, todayIso } from "./calendar-view";
import { calendarChips, CALENDAR_BACKED_TAGS, type CalendarChip } from "./calendar-events";
import companions from "../curriculum/exercise-companions.json";

export interface ClassRow {
  id: string;
  name: string;
  level: string | null;
  periods_per_week?: number;
  teacher_id?: string;
  teacher_name?: string | null;
}

export interface TermInfo {
  term: { id: string; name: string; start_date: string; end_date?: string; week_count?: number };
  weeks?: number[];
  current_week: number | null;
  is_current: boolean;
  next_start?: string | null;
}

/** Where today sits against the term the API returned: inside it, before it starts, or after it ended
 *  (a holiday gap — the API then answers with the term that just finished). */
export type TermPhase = "in" | "before" | "after";
export function termPhase(ti: TermInfo): TermPhase {
  if (ti.is_current) return "in";
  return todayIso() < ti.term.start_date ? "before" : "after";
}

/** "Term 1 · Week 3" (+ " · 28 Sep – 4 Oct" when the week's dates are known and the term is running). Between
 *  terms it says so, rather than naming the week that just ended as if it were this one. */
export function termWeekLabel(ti: TermInfo, range = ""): string {
  const n = termNumber(ti.term);
  const phase = termPhase(ti);
  if (phase === "after" && n !== null && n < 3) return "Between terms";
  const week = phase === "before" ? 1 : ti.current_week ?? "";
  return `${n ? `Term ${n}` : ti.term.name} · Week ${week}${phase === "in" && range ? ` · ${range}` : ""}`;
}

/** One plain line for a term that is not running today. */
export function termNote(ti: TermInfo): string {
  const n = termNumber(ti.term) ?? "";
  if (termPhase(ti) === "before") return t("lesson.preview", { term: n, date: fmtDate(ti.term.start_date) });
  if (ti.next_start && typeof n === "number" && n < 3) return `Holiday — Term ${n + 1} starts ${fmtDate(ti.next_start)}.`;
  return ti.next_start ? `Term ${n} has ended — the next term starts ${fmtDate(ti.next_start)}.` : `Term ${n} has ended.`;
}

/** Which school week the page is about: a week of a running term; before a term starts (its first week, as a
 *  preview); between terms (the next term's first week, "First week"); or after the year (its last week). */
export interface SchoolWeek {
  term: number;
  week: number;
  kind: "week" | "first" | "holiday" | "last";
  /** Between terms: the date the next term starts. */
  nextStart: string | null;
}

/** The school week "now" is, from the server's answer (/api/terms/current) — one answer for every teacher. Null
 *  when the term's name carries no number (screens then say so calmly). */
export function schoolWeekNow(ti: TermInfo | null): SchoolWeek | null {
  const n = ti ? termNumber(ti.term) : null;
  if (!ti || !n) return null;
  const phase = termPhase(ti);
  if (phase === "in") return { term: n, week: ti.current_week ?? 1, kind: "week", nextStart: null };
  if (phase === "before") return { term: n, week: 1, kind: "first", nextStart: null };
  if (n < 3) return { term: n + 1, week: 1, kind: "holiday", nextStart: ti.next_start ?? null };
  return { term: n, week: ti.current_week ?? 1, kind: "last", nextStart: null };
}

/** A grade's lesson for a school week, with the note a page prints when the year plan has no step for it. */
export function gradeLessonFor(cur: GradeCurriculum, week: SchoolWeek | null): { node: CurriculumNode | null; noStep: boolean } {
  if (!week) return { node: cur.nodes[0] ?? null, noStep: false };
  const hit = schoolWeekNode(cur, week.term, week.week);
  return { node: hit.node, noStep: !hit.exact };
}

/** One plain line for a school week the grade's year plan has no step for (Weeks beyond its last step). */
export function noStepLine(week: SchoolWeek | null): string {
  return `School week ${week?.week ?? ""} — no new lesson in the year plan: catch up or review.`;
}

export interface ClassLesson {
  cls: ClassRow;
  grade: number | null;
  cur: GradeCurriculum | null;
  /** This school week's lesson in the grade's year plan — the same for every teacher and class of the grade. */
  node: CurriculumNode | null;
  /** The school week `node` is for (null when the term's name has no number). */
  week: SchoolWeek | null;
  /** The year plan has no step for this school week: `node` is the nearest earlier one (show noStepLine). */
  noStep: boolean;
  /** The class's planned weeks — for ticks, chips and "your plans reach Week 4", never for which week it is. */
  pos: ClassPosition | null;
  hasReal: boolean;
  isPreview: boolean;
}

/** One lesson on a class this week, whoever wrote it (/api/classes/:id/lessons?week=N). */
export interface ClassWeekRow {
  class_id: string;
  lesson_id: string;
  plan_id: string | null;
  week_no: number;
  topic: string;
  status: "not_started" | "in_progress" | "submitted" | "approved";
  node_id: string | null;
}

/** This week's lessons on each class, whoever wrote them. A class the Lead moved to another teacher
 *  keeps the plans its first teacher wrote, so "planned this week" follows the class, not the writer
 *  (the writer's own /api/lessons?scope=week list says which of them the viewer can open). One small
 *  request per class; a failed one counts as none. */
export async function classWeekRows(classes: ClassRow[], weekNo: number | null): Promise<ClassWeekRow[]> {
  if (!weekNo) return [];
  const lists = await Promise.all(classes.map((c) =>
    api<{ lessons: Omit<ClassWeekRow, "class_id">[] }>(`/api/classes/${encodeURIComponent(c.id)}/lessons?week=${weekNo}`)
      .then((r) => r.lessons.map((l) => ({ ...l, class_id: c.id })))
      .catch(() => [] as ClassWeekRow[])));
  return lists.flat();
}

/** This school week's lesson for a class: the grade's year-plan step for the school week (see the header). `at` is a
 *  school week the teacher CHOSE (the Prepare-this-week picker, the worksheet picker); without it the week is "now",
 *  from `termInfo`. A class's plans change nothing here — only its ticks and "your plans reach Week N" line. */
export async function resolveClassLesson(cls: ClassRow, termInfo: TermInfo | null, at?: { term: number; week: number }): Promise<ClassLesson> {
  const grade = gradeFromClass(cls);
  const cur = grade ? await loadGrade(grade) : null;
  const week: SchoolWeek | null = at ? { term: at.term, week: at.week, kind: "week", nextStart: null } : schoolWeekNow(termInfo);
  const isPreview = !!termInfo && !termInfo.is_current;
  let states: NodePlanState[] = [];
  if (cur) {
    try {
      states = (await api<{ nodes: NodePlanState[] }>(`/api/classes/${encodeURIComponent(cls.id)}/position`)).nodes;
    } catch {
      /* position is a nicety — the school week still answers */
    }
  }
  const pos = cur ? classPosition(cur, states) : null;
  const hasReal = !!pos?.position;
  const { node, noStep } = cur ? gradeLessonFor(cur, week) : { node: null, noStep: false };
  return { cls, grade, cur, node, week, noStep, pos, hasReal, isPreview };
}

/** A two-tap "Delete this draft" button (owner ruling A1, 21 Oct 2026) — no browser dialog, the pattern of the observation
 *  page's Discard. The first tap asks (`ask` is the button's new words); the second deletes through
 *  DELETE /api/lessons/:id. `before` runs first (the page settles its autosave so nothing is written after the delete);
 *  `done` runs once the draft is gone; `fail` gets the server's plain sentence when it refuses (a saved worksheet, a
 *  plan sent back to the teacher …) and the button goes back to its first state. */
export function wireDraftDelete(
  button: HTMLButtonElement,
  lessonId: string,
  o: { ask: string; before?: () => Promise<void>; done: () => void; fail: (message: string) => void },
): void {
  const first = button.textContent ?? "";
  let asking: ReturnType<typeof setTimeout> | undefined;
  button.addEventListener("click", async () => {
    if (button.dataset.confirm !== "1") {
      button.dataset.confirm = "1";
      button.textContent = o.ask;
      // a first tap nobody follows up must not leave a loaded button behind: it goes back after a few seconds
      asking = setTimeout(() => { delete button.dataset.confirm; button.textContent = first; }, 6000);
      return;
    }
    clearTimeout(asking);
    button.disabled = true;
    try {
      await o.before?.();
      await api(`/api/lessons/${encodeURIComponent(lessonId)}`, { method: "DELETE" });
      o.done();
    } catch (ex) {
      o.fail((ex as Error).message);
      button.disabled = false;
      delete button.dataset.confirm;
      button.textContent = first;
    }
  });
}

/** "Your plans reach Week 4" — one neutral line for a class that has planned ahead of, or behind, the school week.
 *  No comparison words, no colour: it says where the plans are, not whether that is good. */
export function plansReachLine(pos: ClassPosition | null): string {
  const at = pos?.position;
  return at ? `Your plans reach Term ${at.term} · Week ${at.suggested_week}.` : "";
}

// ── plain words ──────────────────────────────────────────────────────────────────────────────

/** "Term 1 · Week 3" for a node (the syllabus's own week numbering). */
export function nodeWhen(n: CurriculumNode): string {
  return `Term ${n.term} · Week ${n.suggested_week}`;
}

const EXAM: Record<string, string> = {
  starters: "Starters", movers: "Movers", flyers: "Flyers", key: "A2 Key", pet: "B1 Preliminary", ielts: "IELTS",
};
const SKILL: Record<string, string> = {
  listening: "Listening", rw: "Reading & Writing", speaking: "Speaking", reading: "Reading", writing: "Writing",
  mock: "Mock test", intro: "Introduction", "weak-parts": "Weakest parts", "exam-craft": "Exam skills",
  orientation: "Orientation", vocab: "Vocabulary",
};

/** "movers.listening.p2" + "L2" → "Movers · Listening Part 2 (practise)". Unknown pieces are dropped. */
export function taskLabel(taskFamily: string | null | undefined, bloom?: string | null): string {
  if (!taskFamily) return "";
  const [exam, skill, part] = taskFamily.split(".");
  const bits: string[] = [];
  if (exam && EXAM[exam]) bits.push(EXAM[exam]!);
  let s = skill && SKILL[skill] ? SKILL[skill]! : "";
  if (part) {
    const m = /^p(\d+)(?:-(\d+))?$/.exec(part);
    if (m) s += m[2] ? ` Parts ${m[1]}–${m[2]}` : ` Part ${m[1]}`;
    else if (part === "whole") s += " (all parts)";
    else if (part === "full") s = "Full mock test";
    else if (part === "half") s = "Half mock test";
    else if (part === "mini") s = "Mini mock test";
  }
  if (s) bits.push(s.trim());
  // (The Bloom level — learn / practise / apply — is a planning code, not a teacher's word; left off.)
  void bloom;
  return bits.length ? bits.join(" · ") : "";
}

// A node's calendar/event tags in plain words. Codes with no meaning for a teacher are hidden.
const EVENTS: Record<string, string> = {
  "monthly-test": "Monthly test",
  "ptm-1": "Parent–teacher meeting", "ptm-2": "Parent–teacher meeting",
  "staff-training-overlap": "Staff training",
  "sa1-oral": "SA1 speaking test", "sa1-written": "SA1 written test", "sa1-freeze": "Review before SA1 (no new content)",
  "sa2": "SA2 test", "sa2-written": "SA2 written test", "oral-test": "Speaking test",
  "mini-mock": "Mini mock test", "half-mock": "Half mock test", "full-mock": "Full mock test",
  "obs-1": "Classroom observation", "obs-2": "Classroom observation",
  "class-observation-1": "Classroom observation", "class-observation-2": "Classroom observation",
  "christmas-program": "Christmas programme", "class-party": "Class party", "showcase": "Showcase",
  "showcase-prep": "Showcase preparation", "spelling-bee": "Spelling bee", "spelling-bee-window": "Spelling bee",
  "end-of-term-1": "End of Term 1", "end-of-term-2": "End of Term 2", "end-of-term-3": "End of Term 3",
  "start-term-2": "Start of Term 2",
  "gate-sit": "Cambridge exam", "gate-admin": "Cambridge exam registration", "registration": "Cambridge exam registration",
  "registration-deadline": "Cambridge registration deadline", "sit-window-opens": "Cambridge exam window opens",
  "mock-window-opens": "Mock test window opens", "results": "Exam results",
  "graduation": "Graduation", "graduation-window": "Graduation", "prize-giving-day": "Prize-giving day",
  "marking": "Marking", "report": "Report cards",
  "khmer-new-year-days": "Khmer New Year", "khmer-new-year-boundary": "Khmer New Year",
  "khmer-new-year-adjacent": "Khmer New Year", "chinese-new-year-days": "Chinese New Year",
  "chinese-new-year-adjacent": "Chinese New Year", "water-festival": "Water Festival",
  "kings-coronation-day": "King's Coronation Day", "kings-father-commemoration-day": "King Father's Commemoration Day",
  "queens-birthday": "Queen's birthday", "victory-day": "Victory Day", "new-year-day": "New Year's Day",
  "no-school-days": "No school days", "camp-prep": "Camp preparation", "g1-parents-orientation": "Parents' orientation",
  "ne-intensive": "National exam practice", "ne-gtis": "National exam (GTIS)", "g9-exam-no-class": "Grade 9 exam — no class",
  "school-mock-ex-g9": "School mock exam",
};
export function eventLabel(code: string): string | null {
  return EVENTS[code] ?? null;
}
export function eventLabels(codes: readonly string[]): string[] {
  return [...new Set(codes.map(eventLabel).filter((s): s is string => !!s))];
}

/** One chip a screen prints on a week: the school calendar's, or one of the department's own tags. */
export interface WeekChip { label: string; kind: CalendarChip["kind"] | "department"; when?: string }

/** What a grade's week carries at school, as chips (owner ruling A2, 21 Oct 2026). The school calendar owns every
 *  fact it states — tests, term start and end, parent meetings, holidays, camps (src/calendar-events.ts) — so those come
 *  from it, on the week the calendar puts them. The year plan's typed tags for those same facts are no longer printed
 *  (75 of the 176 checkable ones sat on another week); its department tags — mocks, observations, spelling bee,
 *  showcase, Christmas programme, camp preparation, marking, report cards — stay. */
export function weekChips(node: Pick<CurriculumNode, "term" | "suggested_week" | "events">, grade: number | null): WeekChip[] {
  const out: WeekChip[] = grade ? calendarChips(grade, node.term, node.suggested_week) : [];
  const own = eventLabels(node.events.filter((tag) => !CALENDAR_BACKED_TAGS.has(tag)));
  const have = new Set(out.map((c) => c.label));
  for (const label of own) if (!have.has(label)) out.push({ label, kind: "department" });
  return out;
}

/** Book/file titles come from file names ("Let_s Go 4 Student Book - 5th Edition_eden"): clean them
 *  for display only. The underlying title is never changed. */
export function cleanTitle(raw: string): string {
  let s = raw.replace(/Let_s/g, "Let's").replace(/_eden\b/g, "").replace(/\s+-\s+\d+(st|nd|rd|th) Edition/gi, "");
  // "396159-yle-movers-word-list-picture-book-2018 · p.16" → "YLE Movers Word List Picture Book 2018 · p.16"
  s = s.replace(/\b\d{5,}-([a-z0-9-]+)/gi, (_m, slug: string) =>
    slug.split("-").filter(Boolean).map((w) => (/^(yle|pet|ket|cd)$/i.test(w) ? w.toUpperCase() : w[0]!.toUpperCase() + w.slice(1))).join(" "));
  s = s.replace(/_/g, " ").replace(/\s{2,}/g, " ").trim();
  return s;
}

/** The node's aims, without the Teacher's-Guide source tag ("TG: …"). */
/** Plain prose for a teacher: drops the internal set / task-family / Bloom codes some curriculum
 *  lines still carry ("the Flyers number set FLY-11" → "the Flyers number set"), and names a
 *  "(page 2)" pointer as the Teacher Pack page it is. The words themselves are never rewritten. */
export function plainText(raw: string | null | undefined): string {
  if (!raw) return "";
  let s = raw
    // Build notes that slipped into teacher text (teacher review, 15 Oct 2026: "it reads like it is for a
    // computer"): "(The node's own three objectives, nothing added.)", a leading "TG:" source tag.
    .replace(/\s*\((?:[^()]*\bnode'?s own\b[^()]*|[^()]*\bnothing added\b[^()]*)\)/gi, "")
    .replace(/^\s*(?:TG|SB|WB)(?: lesson objectives)?\s*:\s*/i, "")
    // An exam task code says which Cambridge task it is: "task family movers.listening.p1" → "Movers · Listening Part 1".
    .replace(/\b(?:task family )?((?:starters|movers|flyers|ket|pet)\.[a-z0-9.-]*[a-z0-9])\b/gi, (_m, code: string) => {
      const label = taskLabel(code.toLowerCase());
      return label ? label : "the exam task";
    })
    .replace(/\bP([1-9]):/g, "Period $1:")
    .replace(/\b(?:STA|MOV|FLY|KET|PET|RET|RETRIEVAL)-[A-Za-z0-9&]+(?:-[A-Za-z0-9&]+)*(?:\.\.\d+)?\b/g, "")
    .replace(/\bS\d{1,2}-[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*\b/g, "")
    .replace(/\b(?:starters|movers|flyers|ket|pet)\.[a-z0-9.]*[a-z0-9]\b/g, "")
    .replace(/\s*\bat Bloom L\d\b(?:\s*\((?!page)[A-Za-z]+\)|\s*[-–]\s*(?:Learn|Practi[cs]e|Apply|Extend|Stretch)\b)?/g, "")
    .replace(/\(pages? (\d+)(?:\s*(?:-|–|and|&)\s*(\d+))?\)/gi, (_m, a: string, b?: string) => (b ? `(Teacher Pack pp. ${a}–${b})` : `(Teacher Pack p. ${a})`))
    .replace(/\s*\(syllabus title\)/gi, "")
    .replace(/\bSB (pp?\.)/g, "Student Book $1")
    // "(PDF p.18-19)" beside a printed page is the shelf's file page — a teacher holds the printed book
    .replace(/\s*\(PDF pp?\.\s*\d+(?:\s*[-–]\s*\d+)?\)/g, "")
    .replace(/\bT([1-3])\b/g, "Term $1")
    // The remaining shorthand, in a teacher's words (teacher review, 15 Oct 2026).
    .replace(/\bWB (pp?\.)/g, "Workbook $1")
    .replace(/\bTB (pp?\.)/g, "Teacher's Book $1")
    .replace(/\bTG\b/g, "Teacher's Guide")
    .replace(/\breader band:\s*([a-z]+(?:-[a-z]+)*)/gi, (_m, band: string) => band.replace(/-/g, " "))
    .replace(/\b4\/3\/2\b(?! talk)/g, "4/3/2 talk")
    .replace(/\b(t)he phonics rail\b/gi, "$1he phonics practice")
    .replace(/\bCD\s?(\d)\s+(\d\.\d{2})\b/g, "CD $1, track $2")
    .replace(/\bCD(\d)\b/g, "CD $1")
    .replace(/\bretrieval\b/gi, (w) => (w[0] === "R" ? "Review" : "review"))
    .replace(/\bretrieved\b/gi, (w) => (w[0] === "R" ? "Reviewed" : "reviewed"))
    .replace(/\b(Period \d:)\s*[-–]\s*/g, "$1 ");
  s = s.replace(/\(\s*\)/g, "").replace(/\s+([,.;:)])/g, "$1").replace(/:\s*,\s*/g, ": ").replace(/\(\s+/g, "(").replace(/\s{2,}/g, " ").trim();
  // A sentence that lost its source tag starts lower-case ("learning the letter sound groups.").
  if (/^[a-z]/.test(s)) s = s[0]!.toUpperCase() + s.slice(1);
  return s;
}

/** The Teacher Pack pages a materials note points at ("… (page 2). … (page 3).") → [2, 3]. */
export function packPages(note: string | null | undefined): number[] {
  if (!note) return [];
  const out = new Set<number>();
  for (const m of note.matchAll(/\b[Pp]ages? (\d+)(?:\s*(?:-|–|and|&)\s*(\d+))?/g)) {
    const a = Number(m[1]);
    const b = m[2] ? Number(m[2]) : a;
    for (let p = Math.min(a, b); p <= Math.max(a, b) && p - Math.min(a, b) < 6; p++) out.add(p);
  }
  return [...out].sort((x, y) => x - y);
}

export function aimsOf(n: CurriculumNode): string[] {
  return (n.objectives ?? []).map((o) => plainText(o.replace(/^(TG|SB|WB)\s*:\s*/i, "").trim())).filter(Boolean);
}

/** The Cambridge thread's registered vocabulary, not all coursebook vocabulary.
 *  Retrieval weeks review earlier Cambridge words. Null when the node names no set. */
export function wordsOf(reg: VocabRegister | null, thread: NodeThread | undefined): { title: string; words: string[]; review: boolean } | null {
  const vs = thread?.vocab_set;
  if (!vs) return null;
  const sets = resolveSets(reg, vs);
  if (!sets.length) {
    // a retrieval token with no register entry ("RETRIEVAL-…") — say what kind of week it is
    return /^(RET|RETRIEVAL|WEAK|TARGETED)/.test(vs) ? { title: "Review earlier Cambridge vocabulary.", words: [], review: true } : null;
  }
  const words = sets.flatMap((s) => s.words);
  // A word-less set is a REVIEW only when it says so (a retrieval id, or a "Retrieve…/Review…" title);
  // other word-less slots (an orientation, an exam-format week) are not word lists at all. A word-less
  // set's own title is a planning label ("Pre-gate sweep A…", "Consolidate T2 (half mock)"), never
  // teacher-facing, so a review week says only what it is.
  const review = words.length === 0 && (/^(RET|RETRIEVAL|WEAK|TARGETED)/.test(vs) || /^(Retrieve|Review)\b/i.test(sets[0]!.title));
  const title = words.length ? plainText(sets[0]!.title.replace(/\s*\([a-z]\)$/, "")) : "Review earlier Cambridge vocabulary.";
  return { title, words, review };
}

/** A node's teacher-facing book line: "Let's Go 4 · U1 The Great Outdoors · Let's Learn More + Let's Read". */
export function bookLine(cur: GradeCurriculum | null, n: CurriculumNode): string {
  const cb = n.coursebook;
  if (!cb) return "";
  const book = cur ? cur.coursebook.replace(/\s*\(.*?\)\s*/g, " ").replace(/\s*\+.*$/, "").trim() : cb.book ?? "";
  // Blueprint weeks store bare numbers ("1", "2-3", "M2"): say what they are.
  const unit = cb.unit === null || cb.unit === undefined ? "" : /^\d+$/.test(String(cb.unit)) ? `Unit ${cb.unit}` : String(cb.unit).replace(/^M(\d+)$/, "Module $1");
  const lessons = (cb.lessons ?? "").replace(/^(\d+)\s*-\s*(\d+)$/, "Lessons $1–$2").replace(/^(\d+)$/, "Lesson $1");
  return [book, unit, lessons].filter(Boolean).join(" · ");
}

/** The grade's one-line description for headers: "G5 · Let's Go 4 · Movers exam year". */
export function gradeLine(cur: GradeCurriculum): string {
  const book = cur.coursebook.replace(/\s*\(.*?\)\s*/g, " ").replace(/\s{2,}/g, " ").trim();
  const exam = EXAM[cur.exam] ?? cur.exam;
  return `Grade ${cur.grade} · ${book} · ${exam}${cur.gate ? " exam year" : ""}`;
}

/** Sort classes the way a school reads them: by grade number, then name (G1A, G2A … G10A, G11). */
export function byGrade<T extends { name: string; level: string | null }>(a: T, b: T): number {
  const ga = gradeFromClass(a) ?? 99;
  const gb = gradeFromClass(b) ?? 99;
  return ga - gb || a.name.localeCompare(b.name);
}

export { loadVocab };

/** The lesson page's button name for a worksheet's companion PDF, found by its file name: a sheet's note says
 *  "assets-v1/G10-T1-11-Speaking-Photos.pdf"; the lesson page says "Speaking Part 2 photographic stimuli (PDF)". */
export function companionTitle(file: string): string | undefined {
  const name = file.toLowerCase();
  return (companions.companions as { source: string; title: string }[]).find((c) => c.source.toLowerCase().endsWith(`/${name}`))?.title;
}
